import { useState, useCallback } from "react";
import {
  chunkFile,
  generateSHA256,
  calculateTotalParts,
  validateFile,
  retryWithBackoff,
  UploadProgress,
  InitUploadRequest,
  InitUploadResponse,
  FinalizeUploadRequest,
  FinalizeUploadResponse,
  MAX_CONCURRENT_UPLOADS,
} from "@dzajdrop/shared";

interface UseFileUploadOptions {
  apiUrl: string;
  onProgress?: (progress: UploadProgress) => void;
  onSuccess?: (result: FinalizeUploadResponse) => void;
  onError?: (error: string) => void;
}

export function useFileUpload(options: UseFileUploadOptions) {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const uploadFile = useCallback(
    async (
      file: File,
      linkOptions?: {
        customLinkId?: string;
        expirationDays?: number;
        password?: string;
      }
    ) => {
      // Validate file
      const validation = validateFile(file);
      if (!validation.valid) {
        options.onError?.(validation.error || "Invalid file");
        return;
      }

      setIsUploading(true);

      try {
        // Initialize upload
        const initRequest: InitUploadRequest = {
          filename: file.name,
          size: file.size,
        };

        const initResponse = await fetch(
          `${options.apiUrl}/api/upload-chunk?init=1`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(initRequest),
          }
        );

        if (!initResponse.ok) {
          const error = await initResponse.json();
          throw new Error(error.error || "Failed to initialize upload");
        }

        const { uploadId, threadId }: InitUploadResponse =
          await initResponse.json();

        // Set up progress tracking
        const totalParts = calculateTotalParts(file.size);
        let completedParts = 0;
        let uploadedBytes = 0;

        const updateProgress = () => {
          const progressData: UploadProgress = {
            uploadId,
            filename: file.name,
            totalSize: file.size,
            uploadedBytes,
            totalParts,
            completedParts,
            progress: Math.round((completedParts / totalParts) * 100),
          };
          setProgress(progressData);
          options.onProgress?.(progressData);
        };

        updateProgress();

        // Upload chunks sequentially to avoid memory issues
        console.log(`Starting upload of ${totalParts} parts...`);

        for await (const { chunk, partNumber, size } of chunkFile(file)) {
          console.log(`Processing part ${partNumber}/${totalParts}...`);

          try {
            // Generate SHA256 for chunk
            const chunkBuffer = await chunk.arrayBuffer();
            const sha256 = await generateSHA256(chunkBuffer);

            // Upload chunk with retry
            const formData = new FormData();
            formData.append("uploadId", uploadId);
            formData.append("partNumber", partNumber.toString());
            formData.append("sha256", sha256);
            formData.append("chunk", chunk);

            await retryWithBackoff(async () => {
              const response = await fetch(
                `${options.apiUrl}/api/upload-chunk`,
                {
                  method: "POST",
                  body: formData,
                }
              );

              if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || "Failed to upload chunk");
              }
            });

            // Update progress
            completedParts++;
            uploadedBytes += size;
            updateProgress();

            console.log(`Completed part ${partNumber}/${totalParts}`);

            // Give browser a moment to breathe
            if (partNumber < totalParts) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          } catch (error) {
            console.error(`Failed to upload part ${partNumber}:`, error);
            throw error;
          }
        }

        // Finalize upload
        const finalizeRequest: FinalizeUploadRequest = {
          uploadId,
          threadId,
          filename: file.name,
          size: file.size,
          totalParts,
          linkId: linkOptions?.customLinkId,
          exp: linkOptions?.expirationDays
            ? Math.floor(Date.now() / 1000) +
              linkOptions.expirationDays * 24 * 60 * 60
            : undefined,
          pw: linkOptions?.password,
        };

        const finalizeResponse = await fetch(`${options.apiUrl}/api/finalize`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(finalizeRequest),
        });

        if (!finalizeResponse.ok) {
          const error = await finalizeResponse.json();
          throw new Error(error.error || "Failed to finalize upload");
        }

        const result: FinalizeUploadResponse = await finalizeResponse.json();
        options.onSuccess?.(result);
      } catch (error) {
        console.error("Upload error:", error);
        options.onError?.(
          error instanceof Error ? error.message : "Upload failed"
        );
      } finally {
        setIsUploading(false);
        setProgress(null);
      }
    },
    [options]
  );

  return {
    uploadFile,
    isUploading,
    progress,
  };
}
